import { useCallback, useEffect, useState } from 'react';
import useSWR, { unstable_serialize, useSWRConfig } from 'swr';

import { SwimlaneItem } from '../../forces/assetDetails/assetDetails.types';
import { mapResponseToSwimlaneAssetItem } from '../../forces/assetDetails/swimlaneItemMapper';
import { request } from '../../forces/utils/request';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useRevalidateOnTvodTransaction } from '../../hooks/useRevalidateOnTvodTransaction';
import { authService } from '../../utils/auth/AuthService';
import { captureExceptionInSentry } from '../../utils/errorTracker/tracking';
import { Swimlane } from '../pages/types';

import { placeholderSwimlaneItem } from './swimlanePlaceholder/swimlaneItemFixture';
import { isMyTransactionsLink, takeMinSwimlaneItems } from './swimlane.utils';

const swrKey = 'useSwimlane';
export const useSwimlane = (swimlane?: Swimlane, isInViewport = true) => {
  const accessToken = useAuthToken();
  const [swimlaneItems, setSwimlaneItems] = useState<SwimlaneItem[] | undefined>(undefined);
  const { cache } = useSWRConfig();

  let cacheHit = false;
  const fetcherParams =
    swimlane && isInViewport ? ([swimlane.link, accessToken, swrKey, swimlane.type] as const) : null;
  if (fetcherParams && cache instanceof Map) {
    // cache key is joined from fetcherParams
    // NB: serializedKey != swrCacheKey since swr adds "$swr$" to the start
    const serializedKey = unstable_serialize(fetcherParams);
    cacheHit = [...cache.keys()].some(k => k.includes(serializedKey));
  }

  const isMyTransactions = isMyTransactionsLink(swimlane?.link);
  const shouldRevalidate =
    ['ContinueWatching', 'OnTvNow'].includes(swimlane?.type ?? '') || isMyTransactions || !cacheHit;

  const headers = { 'accept-model-version': '2.0' };
  const { data, error, isLoading, mutate } = useSWR(
    fetcherParams,
    ([url, token]) => request<RiksTV.ClientAPI.SwimlaneResponse>({ url, headers }, token),
    {
      revalidateIfStale: shouldRevalidate,
      revalidateOnMount: shouldRevalidate,
      revalidateOnFocus: shouldRevalidate,
      revalidateOnReconnect: false,
    }
  );

  useRevalidateOnTvodTransaction(isMyTransactions, mutate);

  const filterSwimlaneItemById = useCallback((itemId: string) => {
    setSwimlaneItems(items => items?.filter(s => s.id !== itemId));
  }, []);

  useEffect(() => {
    if (swimlane && data) {
      const { style, type, minItemsForShowMore } = swimlane;
      setSwimlaneItems(
        // Don't show more assets than 'minItemsForShowMore' if it is defined
        takeMinSwimlaneItems(
          mapResponseToSwimlaneAssetItem(data, { style, type, loggedIn: authService.isAuthenticated() }),
          style,
          minItemsForShowMore
        )
      );
    }
    if (error) {
      captureExceptionInSentry(error);
    }
  }, [data, error, swimlane]);

  const isPlaceholder = swimlaneItems === undefined;
  const itemsToReturn = isPlaceholder ? [placeholderSwimlaneItem] : swimlaneItems;

  return {
    swimlaneItems: itemsToReturn,
    error,
    filterSwimlaneItemById,
    isPlaceholder,
    isLoading,
  };
};
